import { DateTime } from 'luxon';

/**
 * Time-based menus. A schedule is { days: [0-6] (0 = Sunday), from: 'HH:mm', to: 'HH:mm' }.
 * Overnight windows (from > to, e.g. 22:00–02:00) belong to the day they start.
 */
export function isScheduleActive(schedule, now = new Date(), tz = 'Asia/Kolkata') {
  if (!schedule || schedule.isActive === false) return false;
  const dt = DateTime.fromJSDate(new Date(now)).setZone(tz);
  const minutes = dt.hour * 60 + dt.minute;
  const [fh, fm] = schedule.from.split(':').map(Number);
  const [th, tm] = schedule.to.split(':').map(Number);
  const from = fh * 60 + fm;
  const to = th * 60 + tm;
  const today = dt.weekday % 7; // luxon: Mon=1..Sun=7 → Sun=0
  const yesterday = (today + 6) % 7;
  if (from === to) return schedule.days.includes(today); // all day
  if (from < to) return schedule.days.includes(today) && minutes >= from && minutes < to;
  return (schedule.days.includes(today) && minutes >= from) || (schedule.days.includes(yesterday) && minutes < to);
}

/** An item with no schedules is always on the menu; otherwise any active schedule makes it available. */
export function isItemInSchedule(item, schedulesById, now, tz) {
  if (!item.scheduleIds?.length) return true;
  return item.scheduleIds.some((id) => isScheduleActive(schedulesById.get(String(id)), now, tz));
}

/**
 * Validate selected add-on options against an item's modifier groups (min/max rules).
 * groups: [{ id, name, minSelect, maxSelect, options: [{ id, name, pricePaise, isActive }] }]
 * Returns { ok, error?, selected: [{ groupId, groupName, optionId, name, pricePaise }] }.
 */
export function resolveModifiers(groups, optionIds = []) {
  const wanted = new Set(optionIds.map(String));
  const selected = [];
  for (const g of groups) {
    const picks = g.options.filter((o) => wanted.has(String(o.id)));
    for (const o of picks) {
      if (o.isActive === false) return { ok: false, error: `${o.name} is not available` };
      wanted.delete(String(o.id));
      selected.push({
        groupId: String(g.id),
        groupName: g.name,
        optionId: String(o.id),
        name: o.name,
        pricePaise: o.pricePaise,
      });
    }
    if (picks.length < (g.minSelect ?? 0)) return { ok: false, error: `Choose at least ${g.minSelect} in “${g.name}”` };
    if (g.maxSelect && picks.length > g.maxSelect)
      return { ok: false, error: `Choose at most ${g.maxSelect} in “${g.name}”` };
  }
  if (wanted.size) return { ok: false, error: 'An add-on does not belong to this item' };
  return { ok: true, selected };
}

/** Unit price for an item/variant plus add-ons, with an optional per-outlet price override. */
export function unitPrice({ basePricePaise, overridePricePaise = null, modifiers = [] }) {
  const base = overridePricePaise ?? basePricePaise;
  return base + modifiers.reduce((s, m) => s + (m.pricePaise ?? 0), 0);
}

/**
 * Turn a cart line into a calculation line + snapshot details, from a catalog item whose
 * prices/availability are already resolved for the outlet. Used by the POS cart and by the
 * server (which builds the catalog item from the database), so both price lines identically.
 *
 * item:  { id, name, shortName, priceMode, pricePaise, variants:[{id,name,pricePaise,isActive,outOfStock}],
 *          modifierGroupIds, taxGroupId, hsnSac, foodType, categoryId, kitchenStation, outOfStock, ... }
 * input: { lineId, variantId, modifierOptionIds, qty, discount }
 */
export function priceLine(item, input, { modifierGroupsById, taxGroupsById }) {
  if (!item) return { ok: false, error: 'Item not found' };
  if (item.outOfStock) return { ok: false, error: `${item.name} is out of stock` };
  let base;
  let variant = null;
  if (item.variants?.length) {
    variant = item.variants.find((v) => String(v.id) === String(input.variantId ?? ''));
    if (!variant || variant.isActive === false) return { ok: false, error: `Choose a size for ${item.name}` };
    if (variant.outOfStock) return { ok: false, error: `${item.name} (${variant.name}) is out of stock` };
    base = variant.pricePaise;
  } else {
    if (input.variantId) return { ok: false, error: `${item.name} has no sizes` };
    base = item.pricePaise;
  }
  if (base == null) return { ok: false, error: `${item.name} has no price` };
  const groups = (item.modifierGroupIds ?? []).map((id) => modifierGroupsById.get(String(id))).filter(Boolean);
  const mods = resolveModifiers(groups, input.modifierOptionIds ?? []);
  if (!mods.ok) return mods;
  const tax = taxGroupsById.get(String(item.taxGroupId));
  if (!tax) return { ok: false, error: `${item.name} has no tax group` };
  return {
    ok: true,
    line: {
      lineId: input.lineId,
      qty: input.qty,
      unitPricePaise: unitPrice({ basePricePaise: base, modifiers: mods.selected }),
      priceMode: item.priceMode ?? 'inclusive',
      gstRateBp: tax.gstRateBp ?? 0,
      cessRateBp: tax.cessRateBp ?? 0,
      discount: input.discount ?? null,
    },
    meta: {
      itemId: String(item.id),
      variantId: variant ? String(variant.id) : null,
      name: item.name,
      shortName: item.shortName ?? null,
      variantName: variant?.name ?? null,
      modifiers: mods.selected,
      basePricePaise: base,
      taxGroupId: String(item.taxGroupId),
      taxGroupName: tax.name,
      hsnSac: item.hsnSac ?? null,
      foodType: item.foodType,
      categoryId: item.categoryId ? String(item.categoryId) : null,
      kitchenStation: item.kitchenStation ?? 'kitchen',
      comboComponents: item.comboComponents ?? [],
      note: input.note ?? null,
    },
  };
}

/**
 * Charges that apply to an order type, from an outlet's POS settings.
 * taxGroupsById: Map of tax groups (charge tax is optional; none = 0%).
 */
export function buildCharges(pos, orderType, taxGroupsById = new Map()) {
  const out = [];
  const sc = pos?.serviceCharge;
  if (sc?.enabled && sc.rateBp > 0 && sc.orderTypes?.includes(orderType)) {
    const tg = sc.taxGroupId ? taxGroupsById.get(String(sc.taxGroupId)) : null;
    out.push({
      key: 'service',
      label: `Service charge ${sc.rateBp / 100}%`,
      kind: 'percent',
      valueBp: sc.rateBp,
      gstRateBp: tg?.gstRateBp ?? 0,
      cessRateBp: tg?.cessRateBp ?? 0,
    });
  }
  const pc = pos?.packagingCharge;
  if (pc?.enabled && pc.amountPaise > 0 && pc.orderTypes?.includes(orderType)) {
    const tg = pc.taxGroupId ? taxGroupsById.get(String(pc.taxGroupId)) : null;
    out.push({
      key: 'packaging',
      label: 'Packaging charge',
      kind: pc.mode === 'per_item' ? 'per_item' : 'flat',
      valuePaise: pc.amountPaise,
      gstRateBp: tg?.gstRateBp ?? 0,
      cessRateBp: tg?.cessRateBp ?? 0,
    });
  }
  return out;
}

/** Inter-state supply only when a customer's GSTIN is registered in another state. */
export function supplyTypeFor(outletStateCode, customerGstin) {
  if (!customerGstin || !outletStateCode) return 'intra';
  return customerGstin.slice(0, 2) === outletStateCode ? 'intra' : 'inter';
}
